import { TreeNode } from "@/types/tree";
import { uiStore } from "@/store/store";
import { getDocumentHead } from "@/util/dom";
import { undefined } from "io-ts";
import {
  addOrRerunInlineScriptInHead,
  addUniqueInlineScriptToHead,
  addUniqueScriptToHead
} from "@/lib/canvas/assets/js-in-head";
import { addBuilderElementDependenciesToHead } from "@/lib/canvas/builder-element-dependencies";
import {
  addOrUpdateInlineCSSAtEndOfHead,
  addUniqueStylesheetToEndOfHead
} from "@/lib/canvas/assets/dom-utils";
import { addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead } from "@/lib/canvas/assets/css-in-head/element-styles";

export type ElementPositionAndSize = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function getNodeElementWrapperStyles(domElement: HTMLElement | null): {
  [key: string]: string;
} {
  if (!domElement) {
    return {
      display: "none"
    };
  }

  const canvasPositionAndSize = getCanvasPositionAndSize();
  const elementPositionAndSize = getDomElementPositionAndSize(
    domElement,
    "canvas"
  );

  if (!elementPositionAndSize || !canvasPositionAndSize) {
    return {
      display: "none"
    };
  }

  const { x, y, width, height } = elementPositionAndSize;
  const isWrapperBiggerThanCanvas = width > canvasPositionAndSize.width;

  const outlineThickness = 1;
  const isElementTouchingTheEdgeOfCanvas =
    x === 0 ||
    x + width + outlineThickness >= canvasPositionAndSize.width ||
    y === 0 ||
    y + height + outlineThickness >= canvasPositionAndSize.height;

  // limit the wrapper's size to that of the canvas
  return {
    width: isWrapperBiggerThanCanvas
      ? `${canvasPositionAndSize.width}px`
      : `${width}px`,
    height: `${height}px`,
    top: `${y}px`,
    left: isWrapperBiggerThanCanvas || x < 0 ? "0" : `${x}px`,
    outlineOffset: isElementTouchingTheEdgeOfCanvas ? "-1px" : ""
  };
}

export function getNodeTitlebarStyles(element: TreeNode | null): {
  [key: string]: string;
} {
  if (!element) return {};

  // height of the titlebar + some extra 5 pixels
  const titlebarHeight = 28 + 5;

  const elementPositionAndSize = getTreeNodePositionAndSize(
    element,
    "absolute"
  );

  // if the element is at the top of the screen, move the tittlebar within itself, of below it if it's too small
  if (elementPositionAndSize && elementPositionAndSize.y < titlebarHeight) {
    const isElementTooSmallForTitlebarAtTheTop =
      elementPositionAndSize &&
      elementPositionAndSize.height <= titlebarHeight * 3;

    return {
      top: isElementTooSmallForTitlebarAtTheTop ? "100%" : "0",
      borderRadius: "0 0 4px 4px"
    };
  }

  return {};
}

export function getCanvasPositionAndSize() {
  const canvasElement =
    getBuilderCanvasDocument().getElementById("breakdance_canvas");

  if (!canvasElement) {
    throw "No canvas element";
  }

  return canvasElement.getBoundingClientRect();
}

export function getTreeNodePositionAndSize(
  element: TreeNode,
  relativeTo: "canvas" | "absolute" = "canvas"
): ElementPositionAndSize | false {
  const domElement = getDomElementFromTreeNode(element);

  return domElement
    ? getDomElementPositionAndSize(domElement, relativeTo)
    : false;
}

export function getDomElementPositionAndSize(
  domElement: Element,
  relativeTo: "canvas" | "absolute" = "canvas"
): ElementPositionAndSize {
  const { x, y, width, height } = domElement.getBoundingClientRect();
  const { x: canvasX, y: canvasY } = getCanvasPositionAndSize();

  return {
    x: relativeTo === "canvas" ? x - canvasX : x,
    y: relativeTo === "canvas" ? y - canvasY : y,
    width: width,
    height: height
  };
}

export function getEmptyContainerPositionAndSize(
  relativeTo: "canvas" | "absolute" = "canvas"
): ElementPositionAndSize | false {
  const emptyContainerEl = window.Breakdance.canvas.document.getElementById(
    "breakdance-empty-container"
  );

  if (emptyContainerEl) {
    return getDomElementPositionAndSize(emptyContainerEl, relativeTo);
  }

  return false;
}

export function getDomElementFromTreeNode(node: TreeNode) {
  return getBuilderCanvasDocument().querySelector(
    `[data-node-id="${node.id}"]`
  ) as HTMLElement;
}

export function getElementComputedStyleFromNode(node: TreeNode) {
  const domElement = getDomElementFromTreeNode(node);
  return domElement ? getElementComputedStyle(domElement) : false;
}

// Determine whether the controls should be visible
// Hides the controls of any element that is currently animating
export function shouldHideElementNodeControls(domElement: HTMLElement) {
  return (
    domElement?.classList.contains("is-before") ||
    domElement?.classList.contains("is-animating")
  );
}

export function getElementComputedStyle(domElement: HTMLElement) {
  // @ts-ignore
  return getBuilderCanvasDocument().defaultView.getComputedStyle(
    domElement,
    null
  );
}

export function getActiveElementPositionAndSize() {
  if (!uiStore.activeElement) return null;

  const elementPositionAndSize = getTreeNodePositionAndSize(
    uiStore.activeElement
  );

  if (!elementPositionAndSize) return null;

  return elementPositionAndSize;
}

/*
 * Clear selection on Firefox
 * https://stackoverflow.com/a/29908832
 */
export function clearTextSelection() {
  getBuilderCanvasDocument().getSelection()?.removeAllRanges();
}

export function getBuilderCanvasHead() {
  return getDocumentHead(getBuilderCanvasDocument());
}

export const getBuilderCanvasDocument = () => window.Breakdance.canvas.document;

export type TheSameFunctionButWithoutTheFirstArg<F> = F extends (
  doc: Document,
  ...args: infer A
) => infer R
  ? (...args: A) => R
  : never;

export const bindDocumentToAssetsFn = <F extends Function>(
  fn: F,
  doc: () => Document
): TheSameFunctionButWithoutTheFirstArg<F> => {
  return function (...args: unknown[]) {
    return fn.apply(undefined, [doc(), ...args]);
  } as TheSameFunctionButWithoutTheFirstArg<F>;
};

export function setWindowBreakdanceObjectAssetsFunctionsWithGivenDocumentBounded(
  fnReturningDocumentToBind: () => Document
) {
  window.Breakdance.canvas = {
    ...(window.Breakdance.canvas || {}),
    addUniqueStylesheetToEndOfHead: bindDocumentToAssetsFn(
      addUniqueStylesheetToEndOfHead,
      fnReturningDocumentToBind
    ),
    addUniqueScriptToHead: bindDocumentToAssetsFn(
      addUniqueScriptToHead,
      fnReturningDocumentToBind
    ),
    addUniqueInlineScriptToHead: bindDocumentToAssetsFn(
      addUniqueInlineScriptToHead,
      fnReturningDocumentToBind
    ),
    addOrRerunInlineScriptInHead: bindDocumentToAssetsFn(
      addOrRerunInlineScriptInHead,
      fnReturningDocumentToBind
    ),
    addDependenciesToHead: bindDocumentToAssetsFn(
      addBuilderElementDependenciesToHead,
      fnReturningDocumentToBind
    ),
    addOrUpdateInlineCSSAtEndOfHead: bindDocumentToAssetsFn(
      addOrUpdateInlineCSSAtEndOfHead,
      fnReturningDocumentToBind
    ),
    addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead:
      bindDocumentToAssetsFn(
        addOrUpdateGeneratedElementDependenciesInlineCSSInCanvasHead,
        fnReturningDocumentToBind
      )
  };
}

export function setWindowBreakdanceObjectAssetsFunctionsForBuilderMode() {
  setWindowBreakdanceObjectAssetsFunctionsWithGivenDocumentBounded(() =>
    getBuilderCanvasDocument()
  );
}

export function setWindowBreakdanceCanvasDocument(document: Document) {
  window.Breakdance.canvas = { ...(window.Breakdance.canvas || {}), document };
}
